// IRCTC booking history. No public read-API. Users export their booked
// tickets list as CSV from the IRCTC mobile app (Profile > Booked tickets
// > Export). We parse the CSV and emit a normalised history; useful for
// Wasiyat to know "where I travel often" and for Sehat/Aramgah to add
// travel-day context.

import type { ConnectorAdapter, ConnectorMode, ConnectorResult, ConnectorStatus } from './_types.ts';
import { err, nowIso, ok, shortHash } from './_types.ts';

export type IrctcStatus = 'CONFIRMED' | 'WAITLIST' | 'RAC' | 'CANCELLED';
export type TravelClass = '1A' | '2A' | '3A' | 'SL' | '2S' | 'CC' | 'EC' | 'FC' | 'OTHER';

export interface IrctcBooking {
  readonly pnr: string;
  readonly bookedAt: string;
  readonly travelDate: string;
  readonly trainNumber: string;
  readonly trainName: string;
  readonly fromStationCode: string;
  readonly toStationCode: string;
  readonly classCode: TravelClass;
  readonly passengerCount: number;
  readonly farePaise: number;
  readonly status: IrctcStatus;
}

export interface IrctcSnapshot {
  readonly bookings: readonly IrctcBooking[];
  readonly importedAt: string;
}

export interface IrctcConnectorStatus extends ConnectorStatus {
  readonly id: 'irctc-history';
  readonly bookingCount: number;
}

interface IrctcPersist {
  readonly connectedAt: string;
  readonly bookingCount: number;
  readonly fingerprintHash: string;
}

const STORE_KEY = 'empire/connectors/irctc-history';

export class IrctcHistoryAdapter
  implements ConnectorAdapter<IrctcConnectorStatus, IrctcSnapshot>
{
  readonly id = 'irctc-history';
  readonly displayName = 'IRCTC bookings';
  readonly displayNameHi = 'आईआरसीटीसी यात्रा इतिहास';
  readonly dpdpCategory = 'lifestyle-travel-bookings';
  readonly mode: ConnectorMode;
  #pendingCsv: string | null = null;

  constructor(mode: ConnectorMode = 'test') {
    this.mode = mode;
  }

  async getStatus(): Promise<IrctcConnectorStatus> {
    const persisted = readPersist();
    return {
      id: this.id,
      mode: this.mode,
      connected: persisted !== null,
      lastSyncAt: persisted?.connectedAt ?? null,
      principalIdHash: persisted?.fingerprintHash ?? null,
      bookingCount: persisted?.bookingCount ?? 0,
      notes: 'Upload IRCTC mobile app CSV export. Source: Profile > Booked tickets > Share.',
    };
  }

  async connect(): Promise<ConnectorResult<IrctcConnectorStatus>> {
    return ok(await this.getStatus());
  }

  async disconnect(): Promise<void> {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(STORE_KEY);
  }

  queueCsv(csv: string): void {
    this.#pendingCsv = csv;
  }

  async importPayload(): Promise<ConnectorResult<IrctcSnapshot>> {
    if (!this.#pendingCsv) {
      const mock = this.mockPayload();
      await this.#persist(mock);
      return ok(mock);
    }
    const parsed = parseIrctcCsv(this.#pendingCsv);
    if (!parsed) return err({ code: 'parse', message: 'CSV did not match IRCTC export shape.', retriable: false });
    await this.#persist(parsed);
    return ok(parsed);
  }

  mockPayload(): IrctcSnapshot {
    return {
      bookings: [
        { pnr: '8412345678', bookedAt: '2026-03-12', travelDate: '2026-03-20', trainNumber: '12952', trainName: 'Mumbai Rajdhani', fromStationCode: 'NDLS', toStationCode: 'BCT', classCode: '2A', passengerCount: 1, farePaise: 3245_00, status: 'CONFIRMED' },
        { pnr: '8512345678', bookedAt: '2026-04-04', travelDate: '2026-04-22', trainNumber: '12238', trainName: 'Begampura Express', fromStationCode: 'NDLS', toStationCode: 'BSB', classCode: '3A', passengerCount: 2, farePaise: 2410_00, status: 'CONFIRMED' },
      ],
      importedAt: nowIso(),
    };
  }

  async #persist(snap: IrctcSnapshot): Promise<void> {
    if (typeof localStorage === 'undefined') return;
    const fingerprintHash = await shortHash(snap.bookings.map((b) => b.pnr).join('|'));
    const rec: IrctcPersist = { connectedAt: snap.importedAt, bookingCount: snap.bookings.length, fingerprintHash };
    localStorage.setItem(STORE_KEY, JSON.stringify(rec));
  }
}

function readPersist(): IrctcPersist | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(STORE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as IrctcPersist; } catch { return null; }
}

export function parseIrctcCsv(csv: string): IrctcSnapshot | null {
  const lines = csv.trim().split(/\r?\n/);
  if (lines.length < 2) return null;
  const headers = (lines[0] ?? '').toLowerCase().split(',').map((c) => c.trim());
  const ix = (key: string): number => headers.indexOf(key);
  const need = ['pnr', 'train_number', 'from_station', 'to_station', 'class', 'travel_date'];
  for (const k of need) if (ix(k) < 0) return null;
  const bookings: IrctcBooking[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = (lines[i] ?? '').split(',').map((c) => c.trim());
    const pnr = cols[ix('pnr')] ?? '';
    if (!pnr) continue;
    const cls = (cols[ix('class')] ?? 'OTHER').toUpperCase();
    bookings.push({
      pnr,
      bookedAt: cols[ix('booked_at')] ?? '',
      travelDate: cols[ix('travel_date')] ?? '',
      trainNumber: cols[ix('train_number')] ?? '',
      trainName: cols[ix('train_name')] ?? '',
      fromStationCode: cols[ix('from_station')] ?? '',
      toStationCode: cols[ix('to_station')] ?? '',
      classCode: (['1A','2A','3A','SL','2S','CC','EC','FC'].includes(cls) ? cls : 'OTHER') as TravelClass,
      passengerCount: Number(cols[ix('passengers')] ?? 1) || 1,
      farePaise: Math.round(Number(cols[ix('fare')] ?? 0) * 100),
      status: (['CONFIRMED','WAITLIST','RAC','CANCELLED'].includes((cols[ix('status')] ?? '').toUpperCase())
        ? (cols[ix('status')] ?? '').toUpperCase() : 'CONFIRMED') as IrctcStatus,
    });
  }
  return { bookings, importedAt: nowIso() };
}

export function makeIrctcHistory(mode: ConnectorMode): IrctcHistoryAdapter {
  return new IrctcHistoryAdapter(mode);
}
